"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { thetaApi } from "@/lib/api/theta";
import { useUserMessaging } from "@/lib/hooks/useUserMessaging";
import { useChatDock } from "@/components/ChatDock";

/**
 * The "Send besked" affordance, BY ID — whether a 1:1 already exists and what
 * to do on click. Shared by the full profile page and the profile hover card
 * so the two never drift on what the button actually does (OSO-339).
 *
 * WHETHER TO DRAW THE BUTTON IS NOT DECIDED HERE. `UserProfile.can.message` is
 * the same verdict `POST /conversations` enforces and every caller already has
 * the profile document, so the caller passes it in as `canMessage`; this hook
 * only answers WHERE the button goes. That is why `GET /users/{id}/messaging`
 * is gated on it rather than issued speculatively.
 *
 * It replaces `useSendMessageButton(username)`, which resolved the person
 * through the legacy username-addressed `GET /theta/conversations/with/:username`.
 * `username` is off the v1 wire (O-21) and ten accounts carry a numeric
 * username equal to a DIFFERENT user's id (O-21a), so a handle is not an
 * address any more — the id is.
 */
export function useSendMessageById(userId: number, canMessage: boolean) {
  const router = useRouter();
  const { openConversation } = useChatDock();

  const { data: dmInfo } = useUserMessaging(userId, canMessage);

  const [dmError, setDmError] = useState<string | null>(null);
  const [dmBusy, setDmBusy] = useState(false);

  const handleSendMessage = useCallback(async () => {
    setDmBusy(true);
    setDmError(null);
    try {
      const info = dmInfo ?? (await thetaApi.userMessaging(userId));
      // An EXISTING thread opens even if a new one could not be started —
      // leaving a group does not delete the conversation you already have.
      if (info.conversationId != null) {
        // Desktop opens it in the corner so you keep whatever you were
        // looking at; mobile navigates. openConversation picks per viewport.
        openConversation(info.conversationId);
        return;
      }
      if (!info.canMessage) {
        setDmError(
          "Du kan kun sende beskeder til personer, du deler en gruppe med"
        );
        return;
      }
      /**
       * PREFILLED BY ID. This used to push a bare `/beskeder/ny`, because
       * `?til=` carried a USERNAME and there is no username here any more —
       * `POST /conversations` is registered now and takes `recipientUserIds`
       * (D8), so the composer resolves the person through `GET /users/{id}`
       * and the gap is closed.
       */
      router.push(`/beskeder/ny?til-id=${userId}`);
    } catch {
      setDmError("Samtalen kunne ikke åbnes. Prøv igen.");
    } finally {
      setDmBusy(false);
    }
  }, [dmInfo, userId, router, openConversation]);

  return {
    isRequest: dmInfo?.isRequest === true,
    dmError,
    dmBusy,
    handleSendMessage,
  };
}
